import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Cell } from 'recharts';
const InternalAuditsDashboard: React.FC = () => {
  const kpis = [{
    title: 'Safety Audit Score',
    value: '92/100',
    change: '+3 pts',
    isPositive: true,
    subtitle: 'Overall safety'
  }, {
    title: '5S Score',
    value: '88/100',
    change: '+4 pts',
    isPositive: true,
    subtitle: 'Workplace organization'
  }, {
    title: 'Process Compliance %',
    value: '87%',
    change: '+2%',
    isPositive: true,
    subtitle: 'Process adherence'
  }];
  // Area-wise Safety Score Data
  const areaWiseSafetyData = [{
    area: 'Production Floor',
    score: 90,
    target: 90
  }, {
    area: 'Warehouse',
    score: 94,
    target: 90
  }, {
    area: 'Quality Lab',
    score: 96,
    target: 90
  }, {
    area: 'Maintenance',
    score: 88,
    target: 90
  }, {
    area: 'Office Areas',
    score: 92,
    target: 90
  }, {
    area: 'Chemical Storage',
    score: 85,
    target: 90
  }, {
    area: 'Loading Bay',
    score: 89,
    target: 90
  }];
  // 5S Radar Chart Data
  const fiveS_RadarData = [{
    area: 'Production Floor',
    sort: 90,
    setInOrder: 88,
    shine: 85,
    standardize: 87,
    sustain: 86
  }, {
    area: 'Warehouse',
    sort: 92,
    setInOrder: 90,
    shine: 88,
    standardize: 89,
    sustain: 87
  }, {
    area: 'Quality Lab',
    sort: 95,
    setInOrder: 93,
    shine: 91,
    standardize: 92,
    sustain: 90
  }, {
    area: 'Maintenance',
    sort: 85,
    setInOrder: 83,
    shine: 80,
    standardize: 82,
    sustain: 81
  }, {
    area: 'Office Areas',
    sort: 88,
    setInOrder: 86,
    shine: 84,
    standardize: 85,
    sustain: 83
  }];
  // Process Audit Findings Trend
  const processAuditTrendData = [{
    month: 'Jan',
    findings: 15,
    score: 82,
    resolved: 12
  }, {
    month: 'Feb',
    findings: 12,
    score: 84,
    resolved: 10
  }, {
    month: 'Mar',
    findings: 11,
    score: 85,
    resolved: 9
  }, {
    month: 'Apr',
    findings: 10,
    score: 86,
    resolved: 8
  }, {
    month: 'May',
    findings: 9,
    score: 86,
    resolved: 7
  }, {
    month: 'Jun',
    findings: 8,
    score: 87,
    resolved: 7
  }];
  // Audit Findings Log
  const auditFindingsLog = [{
    id: 'AF-2024-001',
    date: '2024-01-15',
    auditType: '5S Audit',
    area: 'Production Floor',
    finding: 'Tools not returned to designated storage locations',
    category: 'Set in Order',
    severity: 'Medium',
    auditor: 'Quality Team A',
    responsiblePerson: 'Production Supervisor',
    targetDate: '2024-02-15',
    status: 'Closed',
    closureDate: '2024-02-10',
    remarks: 'Shadow boards installed for all tools'
  }, {
    id: 'AF-2024-002',
    date: '2024-01-18',
    auditType: 'Safety Audit',
    area: 'Chemical Storage',
    finding: 'Incompatible chemicals stored together',
    category: 'Chemical Safety',
    severity: 'Critical',
    auditor: 'Safety Team',
    responsiblePerson: 'Warehouse Manager',
    targetDate: '2024-01-25',
    status: 'Closed',
    closureDate: '2024-01-22',
    remarks: 'Chemical segregation implemented with proper labeling'
  }, {
    id: 'AF-2024-003',
    date: '2024-01-20',
    auditType: 'Process Audit',
    area: 'Line 3',
    finding: 'Work instruction not followed for torque settings',
    category: 'Process Compliance',
    severity: 'High',
    auditor: 'Quality Team B',
    responsiblePerson: 'Line Supervisor',
    targetDate: '2024-02-20',
    status: 'In Progress',
    closureDate: '',
    remarks: 'Retraining scheduled for operators'
  }, {
    id: 'AF-2024-004',
    date: '2024-01-22',
    auditType: '5S Audit',
    area: 'Maintenance Shop',
    finding: 'Oil spills not cleaned immediately',
    category: 'Shine',
    severity: 'High',
    auditor: 'Quality Team A',
    responsiblePerson: 'Maintenance Head',
    targetDate: '2024-02-22',
    status: 'In Progress',
    closureDate: '',
    remarks: 'Daily cleaning checklist being implemented'
  }, {
    id: 'AF-2024-005',
    date: '2024-01-25',
    auditType: 'Safety Audit',
    area: 'Warehouse',
    finding: 'Emergency exit blocked by materials',
    category: 'Fire Safety',
    severity: 'Critical',
    auditor: 'Safety Team',
    responsiblePerson: 'Warehouse Supervisor',
    targetDate: '2024-01-28',
    status: 'Closed',
    closureDate: '2024-01-27',
    remarks: 'Exit cleared and floor markings applied'
  }, {
    id: 'AF-2024-006',
    date: '2024-01-28',
    auditType: 'Process Audit',
    area: 'Quality Lab',
    finding: 'Calibration certificates expired for 3 instruments',
    category: 'Measurement System',
    severity: 'High',
    auditor: 'Quality Team B',
    responsiblePerson: 'Lab Manager',
    targetDate: '2024-02-15',
    status: 'In Progress',
    closureDate: '',
    remarks: 'Calibration scheduled with external agency'
  }, {
    id: 'AF-2024-007',
    date: '2024-02-01',
    auditType: '5S Audit',
    area: 'Office Areas',
    finding: 'Outdated documents not removed from filing cabinets',
    category: 'Sort',
    severity: 'Low',
    auditor: 'Quality Team A',
    responsiblePerson: 'Office Manager',
    targetDate: '2024-02-28',
    status: 'Open',
    closureDate: '',
    remarks: 'Document retention policy review in progress'
  }, {
    id: 'AF-2024-008',
    date: '2024-02-03',
    auditType: 'Safety Audit',
    area: 'Production Floor',
    finding: 'Machine guard missing on CNC-5',
    category: 'Machine Safety',
    severity: 'Critical',
    auditor: 'Safety Team',
    responsiblePerson: 'Maintenance Head',
    targetDate: '2024-02-06',
    status: 'In Progress',
    closureDate: '',
    remarks: 'Guard fabrication in progress'
  }, {
    id: 'AF-2024-009',
    date: '2024-02-05',
    auditType: 'Process Audit',
    area: 'Line 1',
    finding: 'First piece inspection not documented',
    category: 'Quality Control',
    severity: 'Medium',
    auditor: 'Quality Team B',
    responsiblePerson: 'QC Inspector',
    targetDate: '2024-02-20',
    status: 'Open',
    closureDate: '',
    remarks: 'Updated inspection forms being rolled out'
  }, {
    id: 'AF-2024-010',
    date: '2024-02-08',
    auditType: '5S Audit',
    area: 'Warehouse',
    finding: 'Inventory labels faded and illegible',
    category: 'Standardize',
    severity: 'Medium',
    auditor: 'Quality Team A',
    responsiblePerson: 'Warehouse Supervisor',
    targetDate: '2024-02-25',
    status: 'Open',
    closureDate: '',
    remarks: 'New labeling system being procured'
  }, {
    id: 'AF-2024-011',
    date: '2024-02-10',
    auditType: 'Safety Audit',
    area: 'Loading Bay',
    finding: 'PPE not worn by forklift operators',
    category: 'PPE Compliance',
    severity: 'High',
    auditor: 'Safety Team',
    responsiblePerson: 'Logistics Manager',
    targetDate: '2024-02-15',
    status: 'In Progress',
    closureDate: '',
    remarks: 'Disciplinary action taken, retraining ongoing'
  }, {
    id: 'AF-2024-012',
    date: '2024-02-12',
    auditType: 'Process Audit',
    area: 'Line 2',
    finding: 'Material traceability not maintained',
    category: 'Traceability',
    severity: 'High',
    auditor: 'Quality Team B',
    responsiblePerson: 'Production Supervisor',
    targetDate: '2024-02-28',
    status: 'Open',
    closureDate: '',
    remarks: 'Barcode scanning system implementation planned'
  }];
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'bg-red-100 text-red-700';
      case 'High':
        return 'bg-orange-100 text-orange-700';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'Low':
        return 'bg-green-100 text-green-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Closed':
        return 'bg-green-100 text-green-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Open':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getScoreColor = (score: number) => {
    if (score >= 90) return '#10b981';
    if (score >= 85) return '#3b82f6';
    if (score >= 80) return '#f59e0b';
    return '#ef4444';
  };
  return <div data-id="element-2134">
      <DescriptionPanel title="Internal Audits Performance" description="Detailed tracking of internal audit programs including 5S, Safety, and Process audits. Monitor compliance levels, identify high-risk findings, and track improvement trends across all operational areas." insights={['Quality Lab achieving highest 5S score at 92/100 - benchmark for other areas', 'Safety compliance at 92% overall - Warehouse leading at 94 score', 'Process audit compliance improved to 87% with findings reduced to 8', '12 audit findings currently tracked - 4 closed, 5 in progress, 3 open']} data-id="element-2135" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-2136">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2137">
          Internal Audit Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2138">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-2139">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-2140">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-2141">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-2142">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-2143">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-2144" /> : <AlertTriangleIcon size={16} className="mr-1" data-id="element-2145" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Area-wise Safety Score and 5S Radar Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-2146">
        {/* Area-wise Safety Score */}
        <Card className="p-6" data-id="element-2147">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2148">
            Area-wise Safety Score
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-2149">
            Safety audit performance across different operational areas
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-2150">
            <BarChart data={areaWiseSafetyData} layout="vertical" data-id="element-2151">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-2152" />
              <XAxis type="number" domain={[0, 100]} tick={{
              fontSize: 11
            }} data-id="element-2153" />
              <YAxis dataKey="area" type="category" width={120} tick={{
              fontSize: 11
            }} data-id="element-2154" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-2155" />
              <Legend data-id="element-2156" />
              <Bar dataKey="score" name="Safety Score" data-id="element-2157">
                {areaWiseSafetyData.map((entry, index) => <Cell key={`cell-${index}`} fill={getScoreColor(entry.score)} data-id="element-2158" />)}
              </Bar>
              <Bar dataKey="target" fill="#10b981" fillOpacity={0.3} name="Target" data-id="element-2159" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2160">
            <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-2161">
              <div className="text-center" data-id="element-2162">
                <div className="text-gray-600" data-id="element-2163">Highest Score</div>
                <div className="text-lg font-bold text-green-600" data-id="element-2164">
                  Quality Lab (96)
                </div>
              </div>
              <div className="text-center" data-id="element-2165">
                <div className="text-gray-600" data-id="element-2166">Avg Score</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-2167">90.6</div>
              </div>
              <div className="text-center" data-id="element-2168">
                <div className="text-gray-600" data-id="element-2169">Below Target</div>
                <div className="text-lg font-bold text-orange-600" data-id="element-2170">2 areas</div>
              </div>
            </div>
          </div>
        </Card>
        {/* 5S Radar Chart */}
        <Card className="p-6" data-id="element-2171">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2172">
            5S Radar Chart
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-2173">
            5S performance across all pillars by area
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-2174">
            <RadarChart data={fiveS_RadarData} data-id="element-2175">
              <PolarGrid stroke="#e5e7eb" data-id="element-2176" />
              <PolarAngleAxis dataKey="area" tick={{
              fontSize: 11,
              fill: '#6b7280'
            }} data-id="element-2177" />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{
              fontSize: 10
            }} data-id="element-2178" />
              <Radar name="Sort" dataKey="sort" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} data-id="element-2179" />
              <Radar name="Set in Order" dataKey="setInOrder" stroke="#10b981" fill="#10b981" fillOpacity={0.6} data-id="element-2180" />
              <Radar name="Shine" dataKey="shine" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} data-id="element-2181" />
              <Radar name="Standardize" dataKey="standardize" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.6} data-id="element-2182" />
              <Radar name="Sustain" dataKey="sustain" stroke="#ec4899" fill="#ec4899" fillOpacity={0.6} data-id="element-2183" />
              <Legend data-id="element-2184" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-2185" />
            </RadarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2186">
            <div className="grid grid-cols-5 gap-2 text-xs" data-id="element-2187">
              <div className="text-center" data-id="element-2188">
                <div className="text-gray-600" data-id="element-2189">Sort</div>
                <div className="text-sm font-bold text-blue-600" data-id="element-2190">90</div>
              </div>
              <div className="text-center" data-id="element-2191">
                <div className="text-gray-600" data-id="element-2192">Set in Order</div>
                <div className="text-sm font-bold text-green-600" data-id="element-2193">88</div>
              </div>
              <div className="text-center" data-id="element-2194">
                <div className="text-gray-600" data-id="element-2195">Shine</div>
                <div className="text-sm font-bold text-orange-600" data-id="element-2196">86</div>
              </div>
              <div className="text-center" data-id="element-2197">
                <div className="text-gray-600" data-id="element-2198">Standardize</div>
                <div className="text-sm font-bold text-purple-600" data-id="element-2199">87</div>
              </div>
              <div className="text-center" data-id="element-2200">
                <div className="text-gray-600" data-id="element-2201">Sustain</div>
                <div className="text-sm font-bold text-pink-600" data-id="element-2202">85</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Process Audit Findings Trend */}
      <Card className="p-6 mb-8" data-id="element-2203">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2204">
          Process Audit Findings Trend
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-2205">
          Track audit findings, resolution rate, and score improvements over
          time
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-2206">
          <LineChart data={processAuditTrendData} data-id="element-2207">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-2208" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-2209" />
            <YAxis yAxisId="left" tick={{
            fontSize: 12
          }} data-id="element-2210" />
            <YAxis yAxisId="right" orientation="right" domain={[75, 100]} tick={{
            fontSize: 12
          }} data-id="element-2211" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-2212" />
            <Legend data-id="element-2213" />
            <Line yAxisId="left" type="monotone" dataKey="findings" stroke="#ef4444" strokeWidth={3} name="New Findings" dot={{
            r: 5
          }} data-id="element-2214" />
            <Line yAxisId="left" type="monotone" dataKey="resolved" stroke="#10b981" strokeWidth={3} name="Resolved" dot={{
            r: 5
          }} data-id="element-2215" />
            <Line yAxisId="right" type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={3} name="Audit Score" dot={{
            r: 5
          }} data-id="element-2216" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2217">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-2218">
            <div className="text-center" data-id="element-2219">
              <div className="text-gray-600" data-id="element-2220">Current Findings</div>
              <div className="text-xl font-bold text-red-600" data-id="element-2221">8</div>
              <div className="text-xs text-gray-500" data-id="element-2222">this month</div>
            </div>
            <div className="text-center" data-id="element-2223">
              <div className="text-gray-600" data-id="element-2224">Resolved</div>
              <div className="text-xl font-bold text-green-600" data-id="element-2225">7</div>
              <div className="text-xs text-gray-500" data-id="element-2226">this month</div>
            </div>
            <div className="text-center" data-id="element-2227">
              <div className="text-gray-600" data-id="element-2228">Resolution Rate</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-2229">87.5%</div>
              <div className="text-xs text-gray-500" data-id="element-2230">avg rate</div>
            </div>
            <div className="text-center" data-id="element-2231">
              <div className="text-gray-600" data-id="element-2232">Audit Score</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-2233">87</div>
              <div className="text-xs text-gray-500" data-id="element-2234">current</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Audit Findings Log */}
      <Card className="p-6" data-id="element-2235">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-2236">
          Audit Findings Log
        </h3>
        <div className="overflow-x-auto" data-id="element-2237">
          <table className="w-full" data-id="element-2238">
            <thead data-id="element-2239">
              <tr className="border-b-2 border-gray-300" data-id="element-2240">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2241">
                  Finding ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2242">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2243">
                  Audit Type
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2244">
                  Area
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2245">
                  Finding
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2246">
                  Category
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2247">
                  Severity
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2248">
                  Auditor
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2249">
                  Responsible
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2250">
                  Target Date
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2251">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2252">
                  Closure Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2253">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody data-id="element-2254">
              {auditFindingsLog.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-2255">
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-2256">
                    {row.id}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2257">
                    {row.date}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2258">
                    {row.auditType}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-2259">
                    {row.area}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2260">
                    {row.finding}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2261">
                    {row.category}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2262">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getSeverityColor(row.severity)}`} data-id="element-2263">
                      {row.severity}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2264">
                    {row.auditor}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2265">
                    {row.responsiblePerson}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2266">
                    {row.targetDate}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2267">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-2268">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2269">
                    {row.closureDate || '-'}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2270">
                    {row.remarks}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-2271">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-2272">
            <div className="text-center" data-id="element-2273">
              <div className="text-gray-600" data-id="element-2274">Total Findings</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-2275">12</div>
              <div className="text-xs text-gray-500" data-id="element-2276">tracked</div>
            </div>
            <div className="text-center" data-id="element-2277">
              <div className="text-gray-600" data-id="element-2278">Critical</div>
              <div className="text-xl font-bold text-red-600" data-id="element-2279">3</div>
              <div className="text-xs text-gray-500" data-id="element-2280">high priority</div>
            </div>
            <div className="text-center" data-id="element-2281">
              <div className="text-gray-600" data-id="element-2282">Closed</div>
              <div className="text-xl font-bold text-green-600" data-id="element-2283">4</div>
              <div className="text-xs text-gray-500" data-id="element-2284">completed</div>
            </div>
            <div className="text-center" data-id="element-2285">
              <div className="text-gray-600" data-id="element-2286">In Progress</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-2287">5</div>
              <div className="text-xs text-gray-500" data-id="element-2288">ongoing</div>
            </div>
            <div className="text-center" data-id="element-2289">
              <div className="text-gray-600" data-id="element-2290">Open</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-2291">3</div>
              <div className="text-xs text-gray-500" data-id="element-2292">pending action</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default InternalAuditsDashboard;